import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable, type MRT_ColumnDef, type MRT_SortingState } from 'mantine-react-table';
import { Checkbox, Group, Text, Button, ActionIcon, Loader, Stack } from '@mantine/core';
import { IconCalculator, IconEdit, IconCertificate, IconFileText } from '@tabler/icons-react';
import './CroppedFieldsGrid.css';
import FieldMapThumbnail from './FieldMapThumbnail';
import CropPickerModal from './CropPickerModal';
import { API_ENDPOINTS } from '../constants/api';
import { calculateDerivedCarbonValues, formatNumber, getSequesteredColor } from '../utils/carbonIntensityCalculations';
import { formatDisplayDate } from '../utils/dateFormat';

interface CroppedFieldsGridProps {
    fieldsWithCrops: any[];
    selectedYear: number;
    isLoading?: boolean;
    userToken?: string;
    selectedOrganizationId?: string;
    engagementLevel?: number;
    onDataRefresh?: () => void;
}

interface FieldData {
    business: string;
    farm: string;
    fieldname: string;
    crop: string;
    crop_id?: string;
    field_id?: string;
    acres: number;
    planting_date: string;
    harvest_date: string;
    bushelsPerAcre: number;
    yield?: number;
    expected_yield?: number | null;
    total_synthetic_n?: number | null;
    tillage?: string | null;
    tdisc_designation?: string | null;
    tdisc_calculated_at?: string | null;
    tdisc_pending?: boolean;
    tillage_event_count?: number;
    cover_crop_used?: string;
    default_ci_score: number;
    ci_score: number;
    total_ghg: number;
    default_total_ghg: number;
    fdcic_evidence_url?: string | null;
    planting_id?: string;
    year?: number;
    geojson?: any;
}

const CroppedFieldsGrid: React.FC<CroppedFieldsGridProps> = ({
    fieldsWithCrops,
    selectedYear,
    isLoading = false,
    userToken,
    selectedOrganizationId,
    engagementLevel = 1,
    onDataRefresh
}) => {
    // Detect mobile screen size
    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
    
    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // State for grouping toggle
    const [enableGrouping, setEnableGrouping] = useState(false);
    
    // State for sorting persistence
    const [sorting, setSorting] = useState<MRT_SortingState>(() => {
        const saved = localStorage.getItem('croppedFieldsGridSorting');
        return saved ? JSON.parse(saved) : [{ id: 'fieldname', desc: false }];
    });
    
    // Save sorting state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('croppedFieldsGridSorting', JSON.stringify(sorting));
    }, [sorting]);
    
    const [calculatingPlantingId, setCalculatingPlantingId] = useState<string | null>(null);
    const [downloadingCertificateId, setDownloadingCertificateId] = useState<string | null>(null);
    const [calculationError, setCalculationError] = useState<string>('');
    const [calculationOverrides, setCalculationOverrides] = useState<Record<string, {
        ci_score?: number;
        total_ghg?: number;
        default_total_ghg?: number;
        default_ci_score?: number;
    }>>({});

    const toFiniteNumber = (value: unknown): number | undefined => {
        if (value === null || value === undefined || value === '') return undefined;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    };
    
    // State for edit modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedFieldForEdit, setSelectedFieldForEdit] = useState<FieldData | null>(null);

    // Handle edit field
    const handleEditField = (fieldData: FieldData) => {
        setSelectedFieldForEdit(fieldData);
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedFieldForEdit(null);
    };

    const handleCropUpdated = () => {
        onDataRefresh?.();
        handleCloseEditModal();
    };

    // Handle certificate download
    const handleDownloadCertificate = async (plantingId: string, certificateLevel: 'level1' | 'level2') => {
        if (!userToken || !plantingId) {
            console.error('Missing userToken or plantingId');
            return;
        }

        setDownloadingCertificateId(plantingId);
        try {
            const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
            const response = await fetch(`${API_ENDPOINTS.CERTIFICATE}/generate-${certificateLevel}${organizationParam}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                },
                body: JSON.stringify({ 
                    planting_id: plantingId
                })
            });

            if (response.ok) {
                // Create blob and download
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `certificate_${certificateLevel}_${plantingId.substring(0, 8)}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                const errorText = await response.text();
                console.error('Error downloading certificate:', errorText);
                throw new Error(`API Error: ${response.status} ${errorText}`);
            }
        } catch (error) {
            console.error('Error downloading certificate:', error);
            // TODO: Show user-friendly error message
        } finally {
            setDownloadingCertificateId(null);
        }
    };

    const handleDownloadEvidence = async (plantingId: string) => {
        if (!userToken || !plantingId) return;
        try {
            const organizationParam = selectedOrganizationId
                ? `?organization_id=${selectedOrganizationId}`
                : '';
            const response = await fetch(
                `${API_ENDPOINTS.CARBON_INTENSITY}/evidence/${plantingId}${organizationParam}`,
                {
                    headers: { Authorization: `Bearer ${userToken}` },
                },
            );
            if (!response.ok) {
                throw new Error(await response.text());
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `fdcic-evidence_${plantingId.substring(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading FD-CIC evidence:', error);
        }
    };

    // Handle carbon calculator for single planting
    const handleCalculateCarbon = async (plantingId: string, plantingYear?: number) => {
        if (!userToken || !plantingId) {
            console.error('Missing userToken or plantingId');
            return;
        }

        setCalculationError('');
        setCalculatingPlantingId(plantingId);
        try {
            // Call the API endpoint for single planting calculation
            const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
            const calculationYear = plantingYear ?? selectedYear;
            const response = await fetch(`${API_ENDPOINTS.CARBON_INTENSITY}/calculate-excel-single${organizationParam}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                },
                body: JSON.stringify({ 
                    planting_id: plantingId,
                    year: calculationYear
                })
            });

            if (response.ok) {
                const payload = await response.json();
                console.log('🌾 Single planting carbon calculation completed:', payload);

                if (payload?.success === false) {
                    const apiError = payload?.error || 'Single planting carbon calculation failed.';
                    setCalculationError(apiError);
                    throw new Error(apiError);
                }

                const result = payload?.result || payload;

                // Update only the clicked row in-place to avoid collapsing/changing the visible list.
                // A full parent refresh was causing row list instability for some users.
                const targetPlantingId = payload?.planting_id || result?.planting_id || plantingId;
                if (targetPlantingId) {
                    setCalculationOverrides((prev) => ({
                        ...prev,
                        [targetPlantingId]: {
                            ci_score: toFiniteNumber(result?.ci_score),
                            total_ghg: toFiniteNumber(result?.total_ghg ?? result?.totalGHG),
                            default_total_ghg: toFiniteNumber(result?.default_total_ghg ?? result?.defaultTotalGHG),
                            default_ci_score: toFiniteNumber(result?.default_ci_score),
                        },
                    }));
                }
            } else {
                const errorText = await response.text();
                console.error('🌾 Error calculating carbon intensity:', errorText);
                const message = `Carbon calculation failed (${response.status}). Please try again.`;
                setCalculationError(message);
                throw new Error(`API Error: ${response.status} ${errorText}`);
            }
        } catch (error) {
            console.error('🌾 Error calculating carbon intensity:', error);
            setCalculationError('Unable to calculate carbon intensity right now. Please try again.');
        } finally {
            setCalculatingPlantingId(null);
        }
    };
    // Filter only fields with crops and transform data
    const rowData = useMemo(() => {

        const filteredData = fieldsWithCrops
            .filter(field => field.crop_id || field.crop) // Check for either crop_id or crop
            .map(field => ({
                business: field.business,
                farm: field.farm,
                fieldname: field.fieldname,
                crop: field.crop || `Crop ID: ${field.crop_id}`, // Use crop if available, otherwise show crop_id
                crop_id: field.crop_id,
                field_id: field.field_id,
                acres: field.crop_acres ? parseFloat(field.crop_acres.toString()) : field.acres,
                planting_date: field.planting_date,
                harvest_date: field.harvest_date,
                bushelsPerAcre: field.yield && field.acres ? parseFloat(field.yield.toString()) / parseFloat(field.acres.toString()) : 0,
                yield: field.yield != null ? parseFloat(field.yield.toString()) : undefined,
                expected_yield: field.expected_yield != null ? parseFloat(field.expected_yield.toString()) : null,
                total_synthetic_n: field.total_synthetic_n != null ? parseFloat(field.total_synthetic_n.toString()) : null,
                tillage: field.tillage ?? null,
                tdisc_designation: field.tdisc_designation ?? null,
                tdisc_calculated_at: field.tdisc_calculated_at ?? null,
                tdisc_pending: Boolean(field.tdisc_pending),
                tillage_event_count: field.tillage_event_count ?? 0,
                cover_crop_used: field.cover_crop_used,
                default_ci_score: field.default_ci_score ? parseFloat(field.default_ci_score.toString()) : 0,
                ci_score: field.ci_score ? parseFloat(field.ci_score.toString()) : 0,
                total_ghg: field.total_ghg ? parseFloat(field.total_ghg.toString()) : 0,
                default_total_ghg: field.default_total_ghg ? parseFloat(field.default_total_ghg.toString()) : 0,
                fdcic_evidence_url: field.fdcic_evidence_url || null,
                planting_id: field.planting_id,
                year: field.year ? Number(field.year) : undefined,
                geojson: field.geojson,
            }))
            .map((row) => {
                const plantingId = row.planting_id || '';
                const override = plantingId ? calculationOverrides[plantingId] : undefined;
                if (!override) return row;

                return {
                    ...row,
                    ci_score: override.ci_score ?? row.ci_score,
                    total_ghg: override.total_ghg ?? row.total_ghg,
                    default_total_ghg: override.default_total_ghg ?? row.default_total_ghg,
                    default_ci_score: override.default_ci_score ?? row.default_ci_score,
                };
            });

    

        return filteredData;
    }, [fieldsWithCrops, calculationOverrides]);

    // Determine if we should hide business unit and farm columns
    const shouldHideBusinessUnit = useMemo(() => {
        const uniqueBusinesses = new Set(rowData.map(field => field.business));
        return uniqueBusinesses.size <= 1;
    }, [rowData]);

    const shouldHideFarm = useMemo(() => {
        const uniqueFarms = new Set(rowData.map(field => field.farm));
        return uniqueFarms.size <= 1;
    }, [rowData]);

    // Column definitions
    const columns: MRT_ColumnDef<FieldData>[] = useMemo(() => {
        const baseColumns: MRT_ColumnDef<FieldData>[] = [];
        
        // Action column - always first, only show when grouping is off
        if (!enableGrouping) {
            baseColumns.push({
                id: 'actions',
                header: 'Actions',
                size: 30,
                enableGrouping: false,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }: { row: any }) => {
                    const plantingId = row.original.planting_id;
                    const isCalculating = calculatingPlantingId === plantingId;
                    
                    return (
                        <Group spacing="xs">
                            {/* Edit Button */}
                            <ActionIcon
                                size="sm"
                                variant="light"
                                color="green"
                                onClick={() => handleEditField(row.original)}
                                title="Edit Field Details"
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                            
                            {/* Calculate Button (hidden for Engagement Level 1) */}
                            {plantingId && engagementLevel > 1 && (
                                <ActionIcon
                                    size="sm"
                                    variant="light"
                                    color="blue"
                                    loading={isCalculating}
                                    onClick={() => handleCalculateCarbon(plantingId, row.original.year)}
                                    title="Calculate Carbon Intensity"
                                >
                                    <IconCalculator size={16} />
                                </ActionIcon>
                            )}

                            {plantingId && row.original.fdcic_evidence_url && (
                                <ActionIcon
                                    size="sm"
                                    variant="light"
                                    color="grape"
                                    onClick={() => handleDownloadEvidence(plantingId)}
                                    title="Calculator evidence PDF"
                                >
                                    <IconFileText size={16} />
                                </ActionIcon>
                            )}
                                
                        </Group>
                    );
                },
            });
        }
        
        // Only show business unit column if there are multiple business units
        if (!shouldHideBusinessUnit) {
            baseColumns.push({
                accessorKey: 'business',
                header: 'Business Unit',
                size: 150,
                enableGrouping: true,
            });
        }
        
        return baseColumns.concat([
            // Only show farm column if there are multiple farms
            ...(shouldHideFarm ? [] : [{
                accessorKey: 'farm',
                header: 'Farm',
                size: 100,
                enableGrouping: true,
            }]),
        {
            accessorKey: 'crop',
            header: 'Crop',
            size: 70,
            enableGrouping: true,
        },
        {
            accessorKey: 'fieldname',
            header: 'Field',
            size: 100,
        },
        {
            accessorKey: 'acres',
            header: 'Acres',
            size: 75,
            Cell: ({ cell }) => cell.getValue<number>()?.toFixed(1) || '0.0',
            aggregationFn: 'sum',
            AggregatedCell: ({ cell }) => (
                <span style={{ color: 'red', fontStyle: 'italic' }}>
                    {cell.getValue<number>()?.toFixed(1) || '0.0'}
                </span>
            ),
        },
        {
            accessorKey: 'planting_date',
            header: 'Date Planted',
            size: 110,
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                if (!value) return '-';
                return formatDisplayDate(value) || '-';
            },
        },
        // Only show harvest date and yield columns for Level 2+ users
        ...(engagementLevel > 1 ? [{
            accessorKey: 'harvest_date',
            header: 'Date Harvested',
            size: 110,
            Cell: ({ cell }: { cell: any }) => {
                const value = cell.getValue();
                if (!value) return '-';
                return formatDisplayDate(value) || '-';
            },
        }] : []),
        ...(engagementLevel > 1 ? [{
            accessorKey: 'bushelsPerAcre',
            header: 'Yield per Acre (bu/ac)',
            size: 70,
            Cell: ({ cell }: { cell: any }) => cell.getValue()?.toFixed(1) || '0.0',
            aggregationFn: 'sum' as any,
            AggregatedCell: ({ cell }: { cell: any }) => (
                <span style={{ color: 'red', fontStyle: 'italic' }}>
                    {cell.getValue()?.toFixed(1) || '0.0'}
                </span>
            ),
        }] : []),
        ...(engagementLevel > 1 ? [{
            accessorKey: 'total_ghg',
            header: 'g GHG Emissions (per bushel)',
            size: 95,
            Cell: ({ cell }: { cell: any }) => {
                const v = Number(cell.getValue());
                return Number.isFinite(v) ? v.toFixed(1) : '0.0';
            },
            aggregationFn: 'mean',
            AggregatedCell: ({ cell }: { cell: any }) => {
                const v = Number(cell.getValue());
                const display = Number.isFinite(v) ? v.toFixed(1) : '0.0';
                return (
                    <span style={{ color: 'red', fontStyle: 'italic' }}>
                        {display}
                    </span>
                );
            },
        }] : []),
       
        {
            accessorKey: 'ghg_per_mj_fixed',
            header: 'Default Carbon Intensity Score',
            size: 90,
            enableColumnFilter: false,
            Cell: ({ row }) => {
              const derived = calculateDerivedCarbonValues(
                row.original.crop,
                row.original.ci_score,
                row.original.default_ci_score,
                row.original.total_ghg,
                row.original.default_total_ghg,
                row.original.bushelsPerAcre
              );
              return formatNumber(derived.ghgPerMjFixed, 1);
            },
        },
        ...(engagementLevel > 1 ? [{
            accessorKey: 'ci_score',
            header: 'Carbon Intensity Score',
            size: 95,
            Cell: ({ cell }: { cell: any }) => {
                const v = Number(cell.getValue());
                return Number.isFinite(v) ? v.toFixed(1) : '0.0';
            },
            aggregationFn: 'mean',
            AggregatedCell: ({ cell }: { cell: any }) => {
                const v = Number(cell.getValue());
                const display = Number.isFinite(v) ? v.toFixed(1) : '0.0';
                return (
                    <span style={{ color: 'red', fontStyle: 'italic' }}>
                        {display}
                    </span>
                );
            },
        }] : []),

        {
            accessorKey: 'kg_ghg_per_mmbtu',
            header: 'kg GHG Emissions per mmBtu',
            size: 120,
            Cell: ({ row }) => {
                const derived = calculateDerivedCarbonValues(
                    row.original.crop,
                    row.original.ci_score,
                    row.original.default_ci_score,
                    row.original.total_ghg,
                    row.original.default_total_ghg,
                    row.original.bushelsPerAcre
                );
                return formatNumber(derived.kgGhgPerMmbtu, 2);
            },
            aggregationFn: 'mean',
            AggregatedCell: ({ cell }) => (
                <span style={{ color: 'red', fontStyle: 'italic' }}>
                    {cell.getValue<number>()?.toFixed(2) || '0.00'}
                </span>
            ),
        },
       /* {
            accessorKey: 't_ghg_per_acre_sequestered',
            header: 't GHG Emissions per Acre Sequestered',
            size: 160,
            Cell: ({ row }) => {
                const derived = calculateDerivedCarbonValues(
                    row.original.crop,
                    row.original.ci_score,
                    row.original.default_ci_score,
                    row.original.total_ghg,
                    row.original.default_total_ghg,
                    row.original.bushelsPerAcre
                );
                const displayValue = formatNumber(derived.tGhgPerAcreSequestered, 4);
                const color = getSequesteredColor(derived.tGhgPerAcreSequestered);
                return <span style={{ color }}>{displayValue}</span>;
            },
            aggregationFn: 'mean',
            AggregatedCell: ({ cell }) => {
                const value = cell.getValue<number>();
                const displayValue = value?.toFixed(4) || '0.0000';
                const color = value && value > 0 ? 'green' : value && value < 0 ? 'red' : 'black';
                return <span style={{ color, fontStyle: 'italic' }}>{displayValue}</span>;
            },
        }, */
        ]);
    }, [enableGrouping, shouldHideBusinessUnit, shouldHideFarm, calculatingPlantingId, downloadingCertificateId]);

    if (isLoading) {
        return (
            <div className="cropped-fields-ag-grid loading">
                <div className="loading-content">
                    <h3>Loading Cropped Fields...</h3>
                    <p>Please wait while we load the data for {selectedYear}.</p>
                </div>
            </div>
        );
    }

    if (rowData.length === 0) {
        return (
            <div className="cropped-fields-ag-grid">
                <div className="no-data">
                    <h3>No Cropped Fields</h3>
                    <p>No fields have crops assigned for {selectedYear}.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="cropped-fields-ag-grid">
            {/* Full-screen loading overlay when calculating carbon intensity */}
            {calculatingPlantingId && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            padding: '40px 50px',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                            textAlign: 'center',
                            minWidth: '320px',
                        }}
                    >
                        <Stack align="center" spacing="md">
                            <Loader size="xl" />
                            <Text size="lg" fw={600}>Calculating Carbon Intensity</Text>
                            <Text size="sm" c="dimmed">Please wait while we process your data...</Text>
                        </Stack>
                    </div>
                </div>
            )}
            {/* Grouping Toggle */}
            <div style={{ marginBottom: '16px', padding: '8px 0' }}>
                {calculationError && (
                    <Text size="sm" c="red" mb="xs">
                        {calculationError}
                    </Text>
                )}
                <Group>
                    <Checkbox
                        checked={enableGrouping}
                        onChange={(event) => setEnableGrouping(event.currentTarget.checked)}
                        label="Enable Grouping"
                        size="sm"
                    />
                    <Text size="sm" c="dimmed">
                        Group by Business Unit, Farm, and Crop
                    </Text>
                </Group>
            </div>
            
            <MantineReactTable
                key={`cropped-fields-grid-${selectedYear}`}
                columns={columns}
                data={rowData}
                enableGrouping={enableGrouping}
                enableColumnFilters={true}
                enableSorting={true}
                enableRowSelection={false}
                enableColumnResizing={true}
                enablePagination={false}
                enableColumnActions={false}
                enableTopToolbar={false}
                enableBottomToolbar={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableHiding={false}
                enableColumnDragging={false}
                enableStickyHeader={!isMobile}
                state={{
                    density: 'xs',
                    grouping: enableGrouping ? ['business', 'farm', 'crop'] : [],
                    expanded: true,
                    sorting: sorting,
                }}
                onSortingChange={setSorting}
                mantineTableProps={{
                    striped: true,
                    highlightOnHover: true,
                    withBorder: true,
                    withColumnBorders: true,
                    horizontalSpacing: 'md',
                    style: {
                        '--mantine-table-row-height': '24px',
                        '--mantine-table-header-height': 'auto',
                    } as React.CSSProperties,
                }}
                mantinePaperProps={{
                    shadow: 'md',
                    radius: 'md',
                    style: {
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      height: 'calc(100vh - 303px)', // Reduced height to ensure scrollbar visibility
                      overflow: 'hidden',
                    },
                  }}
                mantineTableContainerProps={{
                    style: {
                        height: '100%',
                        overflow: 'auto',
                        maxWidth: '100%',
                        minWidth: isMobile ? '100%' : '1200px', // Ensure minimum width for horizontal scrolling
                    },
                }}
            />
            
            {/* Edit Field Modal */}
            {editModalOpen && selectedFieldForEdit && userToken && selectedFieldForEdit.field_id && (
                <CropPickerModal
                    isOpen={editModalOpen}
                    onClose={handleCloseEditModal}
                    fieldId={selectedFieldForEdit.field_id}
                    fieldName={selectedFieldForEdit.fieldname}
                    fieldAcres={selectedFieldForEdit.acres}
                    year={selectedYear}
                    userToken={userToken}
                    onCropAdded={handleCropUpdated}
                    mode="edit"
                    selectedOrganizationId={selectedOrganizationId}
                    engagementLevel={engagementLevel}
                    readonlyCropType={true}
                    existingCropData={{
                        planting_id: selectedFieldForEdit.planting_id || '',
                        crop_id: selectedFieldForEdit.crop_id || '',
                        acres: selectedFieldForEdit.acres.toString(),
                        planting_date: selectedFieldForEdit.planting_date,
                        harvest_date: selectedFieldForEdit.harvest_date,
                        yield: selectedFieldForEdit.yield,
                        bushelsPerAcre: selectedFieldForEdit.bushelsPerAcre,
                        expected_yield: selectedFieldForEdit.expected_yield,
                        total_synthetic_n: selectedFieldForEdit.total_synthetic_n,
                        tillage: selectedFieldForEdit.tillage,
                        tdisc_designation: selectedFieldForEdit.tdisc_designation,
                        tdisc_calculated_at: selectedFieldForEdit.tdisc_calculated_at,
                        tdisc_pending: selectedFieldForEdit.tdisc_pending,
                        tillage_event_count: selectedFieldForEdit.tillage_event_count,
                        cover_crop_used: selectedFieldForEdit.cover_crop_used || 'No Cover Crop',
                    }}
                />
            )}
        </div>
    );
};

export default CroppedFieldsGrid;
